<template>
  <WdsSelect
    v-model="localModelValue"
    :info="fieldTyped.description ?? undefined"
    :required="required"
    :list="optionsList"
    :error="error"
    :disabled="
      props.forceDisabled || (props.forceEnabled ? false : !fieldTyped.viewerCanManageFieldValues)
    "
    :can-deselect="!required"
    searchable
  >
    <template #label>
      {{ fieldTyped.title }}
      <span v-if="required" class="tw-text-indigo-500 tw-font-bold">*</span>
      <WdsTag v-else sm class="tw-ml-1">{{ $t('Optional') }}</WdsTag>
      <CustomFieldVisibilityBadge
        :applies-to="fieldTyped.appliesTo"
        :shared-with-clients="fieldTyped.sharedWithClients"
        :shared-with-staffing-agencies="fieldTyped.sharedWithStaffingAgencies"
        :managed-elsewhere="!fieldTyped.viewerOwnsField"
        :owner-account-name="fieldTyped.account?.name"
      />
    </template>
  </WdsSelect>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsSelect, WdsTag } from '@worksome/wds-components'
import { CustomFieldTypeValueInput } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { FieldableFormCustomFieldFragment } from '@/components/Fieldable/graphql/fragments'
import useIsRequiredField from '@/components/Fieldable/composables/useIsRequiredField'
import { FieldableCustomFieldProps } from '@/components/Fieldable/types'
import { $t } from '@/composables/translation'
import CustomFieldVisibilityBadge from '@/components/Fieldable/CustomFieldVisibilityBadge.vue'

const emit = defineEmits(['update:model-value'])

const props = defineProps<FieldableCustomFieldProps>()

const fieldTyped = computed(() => useFragment(FieldableFormCustomFieldFragment, props.field))
const required = useIsRequiredField(props.field)

const optionsList = computed(() => {
  const values = [] as { value: any; label: string }[]

  fieldTyped.value.customFieldOptions.forEach((customFieldOption) =>
    values.push({
      value: customFieldOption.id,
      label: customFieldOption.value,
    }),
  )
  return values
})

const localModelValue = computed({
  get: () => ({
    value: props.modelValue?.singleSelect?.value,
    label: fieldTyped.value.customFieldOptions?.find(
      (customFieldOption) => customFieldOption.id === props.modelValue?.singleSelect?.value,
    )?.value,
  }),
  set: (selectedOption) => {
    const value = selectedOption === null ? null : selectedOption.value

    emit('update:model-value', {
      singleSelect: {
        id: fieldTyped.value.id,
        value,
      },
    } as CustomFieldTypeValueInput)
  },
})
</script>
